import _ from 'lodash';
import React, { useRef } from 'react';
import { Divider } from 'src/@vymo/ui/atoms';
import Text from 'src/@vymo/ui/atoms/text';
import { Card, NoData } from 'src/@vymo/ui/blocks';
import Form from 'src/@vymo/ui/molecules/form';
import { ReactComponent as VymoAppIcon } from 'src/assets/icons/vymoAppIcon.svg';
import { Keys, locale } from 'src/i18n';
import { FormVersion } from 'src/models/filters/types';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { useAppSelector } from 'src/store/hooks';
import { mapInputsToFormValues } from '../form/queries';
import { getEntityActivitiesBasedOnCode } from './selectors';
import { ActivityTimeCardProps } from './types';
import styles from './index.module.scss';

function ActivityTimelineCard({
  entityId,
  domainType,
  workflowType,
  type,
}: ActivityTimeCardProps) {
  const activities = useAppSelector((state) =>
    getEntityActivitiesBasedOnCode(state, entityId, type),
  );
  const formRef = useRef(null);
  const clonedActivities = _.cloneDeep(activities).sort(
    (a, b) => (b?.lastModifiedTime ?? 0) - (a?.lastModifiedTime ?? 0),
  );

  const revyreConfig = useAppSelector(getRevyreConfig) || {};
  const events = revyreConfig?.[workflowType || domainType]?.events || [];
  const states = revyreConfig?.[workflowType || domainType]?.states || [];

  return (
    <Card data-test-id="history-card" classNames={styles.details__card}>
      <div className={styles.details__activityTimelineCard__header}>
        <Text type="h4">{_.capitalize(type)}</Text>
      </div>
      <Divider />
      {clonedActivities?.length === 0 ? (
        <NoData message={locale(Keys.NO_ACTIVITIES_FOUND)} />
      ) : (
        <div className={styles.details__activityTimelineCard__details}>
          {_.map(clonedActivities, (item, index) => {
            const correspondingEvent = events.find(
              (event) => event?.allowedAction === item?.eventId,
            );

            const correspondingState = states.find(
              (state) => state.id === item?.toState,
            );

            const date = new Date(item?.eventTime);
            const formattedDate = date?.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
            const formattedTime = date?.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });

            return (
              <>
                <div className={styles.details__activityTimelineCard__card}>
                  <div
                    className={
                      styles.details__activityTimelineCard__card__avatar
                    }
                  >
                    <Text type="h5">{formattedDate}</Text>
                    <Text type="label">{formattedTime}</Text>
                  </div>
                  <div
                    className={
                      styles.details__activityTimelineCard__card__details
                    }
                  >
                    <Text type="h6">
                      {correspondingEvent?.actionName ||
                        correspondingState?.name ||
                        _.startCase(_.lowerCase(item?.toState))}
                    </Text>
                    <VymoAppIcon
                      style={{
                        height: '16px',
                        width: '16px',
                      }}
                    />
                  </div>
                </div>

                {type === 'audit' && (
                  <Form
                    ref={formRef}
                    id="auditCardForm"
                    name="auditCardForm"
                    span="1fr 1fr"
                    className={
                      styles.details__activityTimelineCard__card__details__form
                    }
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onChange={(formFields) => {}}
                    config={{
                      version: FormVersion.webRevyre,
                      data: correspondingEvent?.config?.inputs,
                      grouping: [],
                      fieldItemConfig: {
                        showDisabledIcon: true,
                      },
                      viewMode: true,
                    }}
                    value={mapInputsToFormValues(item?.payload)}
                  />
                )}

                {Number(index) !== Number(activities?.length) - 1 && (
                  <Divider />
                )}
              </>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default ActivityTimelineCard;
